// simulator/remote.js
import { fmtDT, normalizeAndSort, hashSnapshot } from './utils.js';
import { listItemTemplate } from './templates.js';
import { withDb } from '../data/idb.js';
import { OFFLINE_MODE_ENABLED, EVENT_ID } from '../core/config.js';

export function initRemote({ UPDATED_FIELD, REMOTE_LIMIT = 5, REMOTE_POLL_MS = 10000 }) {
    // ---- DOM hooks ----
    const remoteListEl = document.getElementById('remoteBadgesList');
    const remoteLastRefreshEl = document.getElementById('remoteLastRefresh');
    const remoteLastCheckedEl = document.getElementById('remoteLastChecked');
    const remoteStatusEl = document.getElementById('remoteStatus');
    const remoteNoticeEl = document.getElementById('remoteNotice');

    let lastRemoteFetch = 0;
    let remoteLastHash = '';
    let __simOffline = !!window.SIMULATOR_OFFLINE;

    // Écoute l'interrupteur offline (simulateur)
    try {
        window.addEventListener('simulator:offline:change', (e) => {
            __simOffline = !!(e && e.detail && e.detail.offline);
            if (__simOffline) {
                remoteStatusEl && (remoteStatusEl.textContent = 'offline');
                remoteStatusEl && (remoteStatusEl.className = 'badge rounded-pill text-bg-secondary');
                remoteNoticeEl && (remoteNoticeEl.innerHTML = '<div class="text-body-secondary small">Remote polling disabled (simulator offline)</div>');
            } else {
                remoteStatusEl && (remoteStatusEl.textContent = 'connecting…');
                remoteStatusEl && (remoteStatusEl.className = 'badge rounded-pill text-bg-warning');
            }
        });
    } catch { }

    function renderRemote(items) {
        if (!remoteListEl) return;
        if (!items?.length) {
            remoteListEl.innerHTML = `<li class="list-group-item"><em class="text-body-secondary">No remote badges found.</em></li>`;
            return;
        }
        remoteListEl.innerHTML = items.map((it) => listItemTemplate(it, UPDATED_FIELD)).join('');
    }

    function fetchRemoteBadges(lwDispatch) {
        if (!remoteListEl) return; // remote UI not present—skip
        try {
            remoteStatusEl && (remoteStatusEl.textContent = 'loading…');
            lwDispatch('checkin.badges.server.lastly-modified.fetch', {
                number_of_badges_lastly_modified: REMOTE_LIMIT,
            });
            lastRemoteFetch = Date.now();
            remoteLastCheckedEl && (remoteLastCheckedEl.textContent = fmtDT(new Date()));

            // Offline mode simulated : we don't try to request the server
            if (__simOffline) {
                remoteStatusEl && (remoteStatusEl.textContent = 'offline');
                remoteStatusEl && (remoteStatusEl.className = 'badge rounded-pill text-bg-secondary');
                return;
            }

        } catch (e) {
            if (remoteNoticeEl) remoteNoticeEl.innerHTML =
                `<span class="text-danger fw-semibold">Remote fetch error:</span> ${e?.message || e}`;
            remoteStatusEl && (remoteStatusEl.textContent = 'error');
        }
    }

    async function handleFetched({ badges = [] } = {}) {
        try {
            const items = normalizeAndSort(badges, UPDATED_FIELD).slice(0, REMOTE_LIMIT);
            const h = hashSnapshot(items, UPDATED_FIELD);
            if (h !== remoteLastHash) {
                remoteLastHash = h;
                renderRemote(items);
                remoteLastRefreshEl && (remoteLastRefreshEl.textContent = fmtDT(new Date()));

                // miroir des données "remote" → IndexedDB locale (comme la live)
                if (OFFLINE_MODE_ENABLED && items.length) {
                    await withDb(async (db) => {
                        const tx = db.transaction('badges', 'readwrite');
                        const store = tx.store;
                        for (const b of items) {
                            const existing = await store.get(b.uuid);
                            const localPendingLogs = Array.isArray(existing?.local_scan_logs)
                                ? existing.local_scan_logs.filter(l => l?.local_scan === true && l?.created_at_ts != null)
                                : [];
                            await store.put({
                                ...(existing || {}),
                                uuid: b.uuid,
                                name: b.name,
                                company: b.company ?? null,
                                enabled: !!b.enabled,
                                scan_count: Number.isFinite(b.scan_count) ? b.scan_count : (existing?.scan_count ?? 0),
                                is_allowed: b.is_allowed,
                                access_details: b.access_details,
                                local_scan_logs: localPendingLogs,
                                synced: localPendingLogs.length === 0,
                                updated_at: b.updated_at,
                            });
                        }
                        await tx.done;
                    });
                    // Notifier immédiatement les vues locales (simulateur) via BroadcastChannel
                    try {
                        const bc = new BroadcastChannel(`checkin-${EVENT_ID}-badges-updates`);
                        bc.postMessage({ type: 'remote-merged', at: Date.now() });
                        bc.close();
                    } catch { }
                }
            }
            remoteStatusEl && (remoteStatusEl.textContent = 'connected');
            remoteStatusEl && (remoteStatusEl.className = 'badge rounded-pill text-bg-success');
        } catch (e) {
            if (remoteNoticeEl) remoteNoticeEl.innerHTML =
                `<span class="text-danger fw-semibold">Remote render error:</span> ${e?.message || e}`;
            remoteStatusEl && (remoteStatusEl.textContent = 'error');
            remoteStatusEl && (remoteStatusEl.className = 'badge rounded-pill text-bg-danger');
        }
    }

    // --- Pause integration (inside initRemote) ---
    let __simPaused = !!(window.badgeTicker?.paused);
    window.addEventListener('badgeTicker:pause', () => { __simPaused = true; });
    window.addEventListener('badgeTicker:resume', () => { __simPaused = false; });

    // Wrap fetchRemoteBadges to no-op when paused
    const __origFetchRemoteBadges = typeof fetchRemoteBadges === 'function' ? fetchRemoteBadges : null;
    if (__origFetchRemoteBadges) {

        fetchRemoteBadges = function (...args) {

            if (__simOffline) {
                try {
                    remoteLastCheckedEl && (remoteLastCheckedEl.textContent = fmtDT(new Date()));
                    remoteStatusEl && (remoteStatusEl.textContent = 'offline');
                    remoteStatusEl && (remoteStatusEl.className = 'badge rounded-pill text-bg-secondary');
                } catch { }
                return;
            }

            if (__simPaused) {
                try {
                    remoteLastCheckedEl && (remoteLastCheckedEl.textContent = fmtDT(new Date()));
                    if (remoteStatusEl) {
                        remoteStatusEl.textContent = 'paused';
                        remoteStatusEl.className = 'badge rounded-pill text-bg-secondary';
                    }
                    if (remoteNoticeEl) {
                        remoteNoticeEl.innerHTML = '';
                    }
                } catch { }
                return;
            }

            return __origFetchRemoteBadges.apply(this, args);

        };
    }

    return {
        fetchRemoteBadges,
        handleFetched,
        get lastRemoteFetch() { return lastRemoteFetch; },
        REMOTE_POLL_MS,
    };
}
